import { useEffect, useRef, useState } from 'react';
import { DemoDirectorService } from '@/services/DemoDirectorService';
import { EventQueueManager } from '@/services/EventQueueManager';
import { CameraPreset } from '@/types/camera.types';
import { QueueState, DirectorResponse } from '@/types/director.types';
import { DemoConfig, TimelineEntry } from '@/types/demo.types';
import { cameraPresets } from '@/data/cameraPresets';

/**
 * Hook for integrating with the Demo Director
 * Loads timeline from demo-config.json and polls MockDirectorService
 * Drop-in replacement for useDirector in demo mode
 */
export function useDemoDirector() {
  const directorService = useRef<DemoDirectorService | null>(null);
  const queueManager = useRef<EventQueueManager | null>(null);
  const [queueState, setQueueState] = useState<QueueState>({
    queue: [],
    currentEvent: null,
    isBusy: false,
  });
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Track current state to compare with new responses
  const currentCameraPreset = useRef<CameraPreset | null>(cameraPresets.default);
  const displayedShotIds = useRef<Set<string>>(new Set());

  // Load timeline from demo config
  useEffect(() => {
    fetch('/demo-config.json')
      .then(res => res.json())
      .then((data: DemoConfig) => {
        console.log('[useDemoDirector] Loaded demo config with', data.timeline.length, 'timeline entries');
        setTimeline(data.timeline);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('[useDemoDirector] Failed to load demo config:', error);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    // Wait for timeline to load
    if (isLoading || timeline.length === 0) return;

    // Initialize services
    directorService.current = new DemoDirectorService(timeline);
    queueManager.current = new EventQueueManager();

    // Reset displayedShotIds to prevent React StrictMode double-mount issues
    displayedShotIds.current.clear();

    // Subscribe to queue state changes
    queueManager.current.onStateChangeCallback((state) => {
      setQueueState(state);
    });

    // Helper to compare camera presets
    const camerasAreEqual = (a: CameraPreset | null, b: CameraPreset | null): boolean => {
      if (!a || !b) return a === b;
      return (
        a.position.x === b.position.x &&
        a.position.y === b.position.y &&
        a.position.z === b.position.z &&
        a.target.x === b.target.x &&
        a.target.y === b.target.y &&
        a.target.z === b.target.z
      );
    };

    // Handle Director updates
    const handleDirectorUpdate = (response: DirectorResponse) => {
      if (!queueManager.current) return;

      // Early return if Director has no new data
      if (!response.cameraPreset && response.shots.length === 0) {
        return;
      }

      // Check if there's any new data
      const hasNewCamera = !camerasAreEqual(response.cameraPreset, currentCameraPreset.current);
      const newShots = response.shots.filter(shot => !displayedShotIds.current.has(shot.id));

      // Apply rules based on what's new
      if (!hasNewCamera && newShots.length === 0) {
        // Rule: Same camera, no new shots - Do nothing
        return;
      }

      if (!hasNewCamera && newShots.length > 0) {
        // Rule: Same camera, new shots - Queue shot traces only
        console.log(`[DemoDirector] Same camera, adding ${newShots.length} new shots: [${newShots.map(s => s.id).join(', ')}]`);
        newShots.forEach(shot => {
          queueManager.current!.enqueueShotTrace(shot);
          displayedShotIds.current.add(shot.id);
        });
        return;
      }

      if (hasNewCamera && newShots.length === 0) {
        // Rule: New camera, no shots - Queue camera move
        console.log('[DemoDirector] Moving to new camera position');
        if (response.cameraPreset) {
          queueManager.current.enqueueCameraMove(response.cameraPreset);
          currentCameraPreset.current = response.cameraPreset;
        }
        return;
      }

      if (hasNewCamera && newShots.length > 0) {
        // Rule: New camera with shots - Queue camera move first, then shots
        console.log(`[DemoDirector] Moving to new camera, then showing ${newShots.length} shots: [${newShots.map(s => s.id).join(', ')}]`);
        if (response.cameraPreset) {
          queueManager.current.enqueueCameraMove(response.cameraPreset);
          currentCameraPreset.current = response.cameraPreset;
        }
        newShots.forEach(shot => {
          queueManager.current!.enqueueShotTrace(shot);
          displayedShotIds.current.add(shot.id);
        });
        return;
      }
    };

    // Start polling for updates
    directorService.current.connect(handleDirectorUpdate);

    // Cleanup
    return () => {
      directorService.current?.disconnect();
    };
  }, [timeline, isLoading]);

  // Reset function (compatible with WebSocket version but only resets local state)
  const resetDirector = () => {
    directorService.current?.reset();
    console.log('[DemoDirector] Timeline reset');
  };

  // Reset function to clear frontend Director state
  const resetDirectorState = () => {
    displayedShotIds.current.clear();
    queueManager.current?.reset();
    currentCameraPreset.current = cameraPresets.default;
    console.log('[DemoDirector] Frontend state reset');
  };

  return {
    queueManager: queueManager.current,
    queueState,
    displayedShotIds: Array.from(displayedShotIds.current),
    resetDirector,
    resetDirectorState,
  };
}
